"use client";

import { useRef, useState, useMemo } from "react";
import PropTypes from "prop-types";
import "./UpcomingCard.css";
import { Calendar } from "lucide-react";
import { useTranslation } from "react-i18next";
import userIcon from "../../../assets/icons/user.png";

const UpcomingCard = ({
  appointment,
  isFirst,
  onReschedule,
  onCancel,
  busy,
}) => {
  const { t, i18n } = useTranslation();

  const imgRef = useRef(null);
  const [imgError, setImgError] = useState(false);

  const isRTL = i18n.language?.startsWith("ar");

  const getFullName = (person) => {
    if (!person || typeof person !== "object") return "";

    const firstName =
      person.firstName ||
      person.firstname ||
      person.prenom ||
      person.first_name ||
      person.user?.firstName ||
      person.user?.firstname ||
      person.user?.prenom ||
      person.user?.first_name ||
      "";

    const lastName =
      person.lastName ||
      person.lastname ||
      person.nom ||
      person.last_name ||
      person.user?.lastName ||
      person.user?.lastname ||
      person.user?.nom ||
      person.user?.last_name ||
      "";

    return (
      person.fullName ||
      person.fullname ||
      person.name ||
      person.displayName ||
      person.user?.fullName ||
      person.user?.fullname ||
      person.user?.name ||
      person.user?.displayName ||
      `${firstName} ${lastName}`.trim()
    );
  };

  const providerName = useMemo(() => {
    const doctorObject =
      appointment?.doctor ||
      appointment?.medecin ||
      appointment?.provider ||
      appointment?.doctorInfo ||
      appointment?.medecinInfo ||
      appointment?.doctorData ||
      appointment?.medecinData;

    const nameFromObject = getFullName(doctorObject);

    return (
      appointment?.providerDisplay ||
      appointment?.providerName ||
      appointment?.doctorName ||
      appointment?.medecinName ||
      appointment?.doctorFullName ||
      appointment?.medecinFullName ||
      nameFromObject ||
      ""
    );
  }, [appointment]);

  const displayProviderName = useMemo(() => {
    const name = String(providerName || "").trim();

    if (!name) {
      return t("appointmentss.common.doctorFallback", "Doctor");
    }

    if (/^(dr\.?|doctor)\s+/i.test(name)) {
      return name;
    }

    return `Dr. ${name}`;
  }, [providerName, t]);

  const providerTitle = useMemo(() => {
    const doctorObject =
      appointment?.doctor ||
      appointment?.medecin ||
      appointment?.provider ||
      appointment?.doctorInfo ||
      appointment?.medecinInfo;

    return (
      appointment?.providerTitle ||
      appointment?.specialtyDisplay ||
      appointment?.specialty ||
      doctorObject?.specialty ||
      doctorObject?.specialite ||
      doctorObject?.speciality ||
      doctorObject?.specialtyName ||
      doctorObject?.specialiteName ||
      t("appointmentss.common.providerFallback", "Provider")
    );
  }, [appointment, t]);

  const dateText =
    appointment?._display?.dateText ||
    appointment?.date ||
    (appointment?.startTime
      ? new Date(appointment.startTime).toLocaleDateString()
      : "");

  const timeText =
    appointment?._display?.timeText ||
    appointment?.time ||
    (appointment?.startTime
      ? new Date(appointment.startTime).toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        })
      : "");

  const providerImage =
    appointment?.providerImage ||
    appointment?.image ||
    appointment?.doctor?.image ||
    appointment?.doctor?.photo ||
    appointment?.doctor?.profileImage ||
    appointment?.doctor?.avatar ||
    appointment?.doctor?.user?.image ||
    appointment?.doctor?.user?.photo ||
    appointment?.medecin?.image ||
    appointment?.medecin?.photo ||
    appointment?.medecin?.profileImage ||
    appointment?.medecin?.avatar ||
    appointment?.medecin?.user?.image ||
    appointment?.medecin?.user?.photo ||
    null;

  const handleImgError = () => setImgError(true);

  return (
    <div
      className={`upcoming-card ${isFirst ? "first-card" : ""}`}
      dir={isRTL ? "rtl" : "ltr"}
    >
      <div className="provider-avatar">
        {!imgError && providerImage ? (
          <img
            ref={imgRef}
            src={providerImage}
            alt={displayProviderName}
            onError={handleImgError}
            loading="lazy"
          />
        ) : (
          <img
            src={userIcon}
            alt={t("appointmentss.common.doctorIconAlt", "Doctor")}
            className="provider-fallback-icon"
            loading="lazy"
          />
        )}
      </div>

      <div className="appointment-details">
        <div className="provider-section">
          <div className="provider-info">
            <span className="provider-name">{displayProviderName}</span>
          </div>

          <div className="provider-title">{providerTitle}</div>
        </div>

        <div className="appointment-time">
          <Calendar size={16} style={{ marginInlineEnd: 8 }} />
          <span>
            {dateText} {timeText}
          </span>
        </div>

        {appointment?.reason && (
          <div className="appointment-reason">{appointment.reason}</div>
        )}
      </div>

      <div className="appointment-actions">
        <button
          className="reschedule-btn outline"
          type="button"
          disabled={busy}
          onClick={() => onReschedule?.(appointment)}
        >
          {t("appointmentss.card.reschedule", "RESCHEDULE")}
        </button>

        <button
          className="reschedule-btn filled"
          type="button"
          disabled={busy}
          onClick={() => onCancel?.(appointment)}
        >
          {busy
            ? t("appointmentss.common.loading", "...")
            : t("appointmentss.card.cancel", "CANCEL")}
        </button>
      </div>
    </div>
  );
};

UpcomingCard.propTypes = {
  appointment: PropTypes.object,
  isFirst: PropTypes.bool,
  onReschedule: PropTypes.func,
  onCancel: PropTypes.func,
  busy: PropTypes.bool,
};

export default UpcomingCard;